/**
 * VoiceRecorder – records audio via MediaRecorder API
 * with waveform sampling for Matrix voice messages.
 *
 * Usage:
 *   const recorder = new VoiceRecorder({ onTick, onStart, onStop, onCancel });
 *   await recorder.start();
 *   const { file, duration, waveform } = await recorder.stop();
 *   recorder.cancel();   // discard recording
 */
export class VoiceRecorder {
  constructor({ onTick, onStart, onStop, onCancel } = {}) {
    this.onTick = onTick;
    this.onStart = onStart;
    this.onStop = onStop;
    this.onCancel = onCancel;

    this._recorder = null;
    this._stream = null;
    this._chunks = [];
    this._startTime = 0;
    this._duration = 0;
    this._timerInterval = null;
    this._recording = false;

    // Waveform sampling
    this._audioContext = null;
    this._analyser = null;
    this._sourceNode = null;
    this._waveformSamples = [];
    this._waveformInterval = null;
  }

  get isRecording() {
    return this._recording;
  }

  get duration() {
    return this._duration;
  }

  static formatDuration(seconds) {
    // A file without a length header reports Infinity; show 0:00, not "Infinity:NaN".
    if (!Number.isFinite(seconds) || seconds < 0) seconds = 0;
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${s.toString().padStart(2, "0")}`;
  }

  /**
   * Determine best supported MIME type for audio recording.
   *
   * AAC in MP4 first: its header carries the length, and every browser the
   * chat runs in plays it, iOS included. WebM/Opus from MediaRecorder has no
   * length (Chrome reports Infinity, WebKit NaN until it has read the whole
   * file) and is what WebKit plays least reliably — yet Chrome and current
   * Safari both support it, so with WebM first every recording came out that
   * way. It stays the fallback for browsers without MP4 recording (Firefox).
   */
  static _getMimeType() {
    const candidates = [
      "audio/mp4;codecs=mp4a.40.2",
      "audio/mp4",
      "audio/webm;codecs=opus",
      "audio/ogg;codecs=opus",
      "audio/webm",
    ];
    for (const mime of candidates) {
      if (typeof MediaRecorder !== "undefined" && MediaRecorder.isTypeSupported(mime)) {
        return mime;
      }
    }
    return ""; // let the browser decide
  }

  /**
   * Start recording audio.
   * Requests microphone permission if not already granted.
   */
  async start() {
    if (this._recording) return;

    this._stream = await navigator.mediaDevices.getUserMedia({ audio: true });

    const mimeType = VoiceRecorder._getMimeType();
    const options = mimeType ? { mimeType } : {};
    this._recorder = new MediaRecorder(this._stream, options);
    this._chunks = [];

    this._recorder.addEventListener("dataavailable", (e) => {
      if (e.data.size > 0) this._chunks.push(e.data);
    });

    // Set up waveform sampling via Web Audio API
    this._setupWaveformSampling();

    this._recorder.start(250); // collect data every 250ms
    this._recording = true;
    this._startTime = Date.now();
    this._duration = 0;
    this._waveformSamples = [];

    // Timer ticks every 200ms for smooth display
    this._timerInterval = setInterval(() => {
      this._duration = (Date.now() - this._startTime) / 1000;
      if (this.onTick) this.onTick(this._duration);
    }, 200);

    if (this.onStart) this.onStart();
  }

  /**
   * Set up an AnalyserNode to sample waveform amplitude during recording.
   */
  _setupWaveformSampling() {
    try {
      this._audioContext = new (window.AudioContext || window.webkitAudioContext)();
      // Safari may create the context suspended; a suspended analyser reads
      // silence for the whole recording.
      this._audioContext.resume?.().catch(() => {});
      this._analyser = this._audioContext.createAnalyser();
      // 2048 samples ≈ 43ms of audio per reading (at 48kHz); 512 was an 11ms
      // snapshot every quarter second that a short peak could fall between.
      this._analyser.fftSize = 2048;
      this._sourceNode = this._audioContext.createMediaStreamSource(this._stream);
      this._sourceNode.connect(this._analyser);

      const dataArray = new Uint8Array(this._analyser.fftSize);

      // Ten readings per second; stop() condenses them to the 64 the message
      // carries, keeping the loudest of each stretch.
      this._waveformInterval = setInterval(() => {
        this._analyser.getByteTimeDomainData(dataArray);
        // Calculate RMS amplitude as a single value 0–1024 (Matrix waveform range)
        let sum = 0;
        for (let i = 0; i < dataArray.length; i++) {
          const v = (dataArray[i] - 128) / 128; // normalize to -1..1
          sum += v * v;
        }
        const rms = Math.sqrt(sum / dataArray.length);
        // Scale to 0–1024 range (Matrix MSC1767 waveform spec)
        const sample = Math.min(1024, Math.round(rms * 1024 * 3)); // amplify slightly
        this._waveformSamples.push(sample);
      }, 100);
    } catch (e) {
      console.warn("[VoiceRecorder] Could not set up waveform sampling:", e);
    }
  }

  /**
   * Stop recording and return the result.
   * @returns {Promise<{ file: File, duration: number, waveform: number[], mimeType: string }>}
   */
  stop() {
    return new Promise((resolve) => {
      if (!this._recording || !this._recorder) {
        resolve(null);
        return;
      }

      // Measured here, not taken from the last timer tick: the tick lags by up
      // to 200ms, and a throttled or suspended timer (a WebView in the
      // background) would leave the recording with a stale length.
      this._duration = (Date.now() - this._startTime) / 1000;

      this._recorder.addEventListener("stop", () => {
        this._recording = false;
        this._cleanup();

        const mimeType = this._recorder.mimeType || "audio/webm";
        const blob = new Blob(this._chunks, { type: mimeType });
        const ext = mimeType.includes("mp4") ? "m4a" : mimeType.includes("ogg") ? "ogg" : "webm";
        const file = new File([blob], `voice-message.${ext}`, { type: mimeType });

        const duration = this._duration;

        // Resample the waveform to the 64 values the message carries
        const waveform = this._resampleWaveform(this._waveformSamples, 64);

        if (this.onStop) this.onStop({ file, duration, waveform, mimeType });
        resolve({ file, duration, waveform, mimeType });
      }, { once: true });

      this._recorder.stop();
    });
  }

  /**
   * Cancel the recording and discard all data.
   */
  cancel() {
    if (!this._recording) return;
    this._recording = false;

    if (this._recorder && this._recorder.state !== "inactive") {
      this._recorder.stop();
    }

    this._cleanup();
    this._chunks = [];
    this._waveformSamples = [];

    if (this.onCancel) this.onCancel();
  }

  /**
   * Cleanup timer, audio context, and microphone stream.
   */
  _cleanup() {
    if (this._timerInterval) {
      clearInterval(this._timerInterval);
      this._timerInterval = null;
    }
    if (this._waveformInterval) {
      clearInterval(this._waveformInterval);
      this._waveformInterval = null;
    }
    if (this._sourceNode) {
      this._sourceNode.disconnect();
      this._sourceNode = null;
    }
    if (this._audioContext) {
      this._audioContext.close().catch(() => {});
      this._audioContext = null;
    }
    if (this._stream) {
      this._stream.getTracks().forEach((track) => track.stop());
      this._stream = null;
    }
  }

  /**
   * Resample a waveform array to exactly `targetLength` values that span the
   * whole recording. Each value is the loudest reading of its stretch; a
   * recording with fewer readings than that is stretched, never padded — the
   * padding zeros used to sit at the end, so anything under 16 seconds showed
   * its sound in the first bars and a flat line where the rest of it was.
   */
  _resampleWaveform(samples, targetLength) {
    if (samples.length === 0) return new Array(targetLength).fill(0);

    const result = [];
    const bucketSize = samples.length / targetLength;
    for (let i = 0; i < targetLength; i++) {
      const start = Math.floor(i * bucketSize);
      const end = Math.max(start + 1, Math.floor((i + 1) * bucketSize));
      let max = 0;
      for (let j = start; j < end && j < samples.length; j++) {
        if (samples[j] > max) max = samples[j];
      }
      result.push(max);
    }
    return result;
  }
}
